// app.jsx — top-level App shell: route (home / room), drawer nav, theme.
//
// Ported from design/new design/app.jsx. Two adjustments from the design:
//  - useRoomState replaced with usePanelRooms(hass) — overlays live HA
//    state on top of the mock seed. Caller (main.jsx) passes hass.
//  - Auto-theme prefers hass.themes.darkMode when available (lets users
//    control dark mode from their HA profile / OS). Falls back to local
//    hour-based useAutoTheme for standalone dev preview.

import * as React from 'react';
import {
  useViewport, useAutoTheme, useComfort, useClock,
} from './shared.jsx';
import RoomView, {
  A_LIGHT, A_DARK, A_FONT, ALabel, Segmented, fs,
} from './direction-a.jsx';
import { RADIUS } from './theme.js';
import HomeView from './home.jsx';
import { MusicShell, BuilderShell } from './music/index.jsx';
import { TVShell } from './tv/index.jsx';
import { LightsShell } from './lights/index.jsx';
import { ShadesShell } from './shades/index.jsx';
import { ClimateShell } from './climate/index.jsx';
import { GuestModeShell } from './guest-mode/index.jsx';
import { VacuumShell } from './vacuum/index.jsx';
import { WeatherShell, useWeather } from './weather/index.jsx';
import { PowerShell, usePower } from './power/index.jsx';
import { PlantsShell, usePlants } from './plants/index.jsx';
import { PoolShell } from './pool/index.jsx';
import { areasForPanelRoom } from './vacuum/policy.js';
import { usePanelRooms } from './hooks/use-panel-rooms.jsx';

const { useState, useEffect } = React;

function Drawer({ T, open, onClose, rooms, route, onNavigate, onOpenWeather, weatherOpen, onOpenPower, powerOpen, onOpenPlants, plantsOpen, onOpenPool, poolOpen, onOpenMusic, musicOpen, onOpenBuilder, builderOpen, onOpenGuestMode, guestOpen, guestActive, onOpenVacuum, vacuumOpen, vacuumBusy, dark, onToggleDark, autoDark, onToggleAuto, comfort, onToggleComfort, hass }) {
  useEffect(() => {
    if (!open) return;
    const onK = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onK);
    return () => window.removeEventListener('keydown', onK);
  }, [open, onClose]);

  return (
    <div aria-hidden={!open} style={{
      position: 'fixed', inset: 0, zIndex: 50,
      pointerEvents: open ? 'auto' : 'none',
    }}>
      {/* Scrim */}
      <div onClick={onClose} style={{
        position: 'absolute', inset: 0, background: T.scrim,
        opacity: open ? 1 : 0, transition: 'opacity .22s ease',
      }}/>
      {/* Panel — padding picks up env(safe-area-inset-*) on the
          relevant sides so the drawer header isn't pushed under the
          notch on iPhone. The drawer's parent scrim already spans the
          full viewport (dark fill under iOS chrome is fine). */}
      <aside style={{
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: 'min(320px, 86vw)',
        background: T.paper,
        boxShadow: open ? T.shadow : 'none',
        transform: open ? 'translateX(0)' : 'translateX(-104%)',
        transition: 'transform .26s cubic-bezier(.2,.7,.2,1)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
        padding: 'calc(22px + env(safe-area-inset-top)) 22px calc(18px + env(safe-area-inset-bottom)) calc(22px + env(safe-area-inset-left))',
        color: T.ink, fontFamily: A_FONT.sans,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: A_FONT.serif, fontSize: 32, color: T.ink, lineHeight: 1, fontWeight: 500, letterSpacing: '-.01em' }}>
              Wallace St
            </div>
            <ALabel T={T} style={{ marginTop: 8 }}>Home Assistant</ALabel>
          </div>
          <button onClick={onClose} aria-label="Close menu" style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            border: 'none', background: T.cardSoft,
            color: T.ink, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>

        <nav style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <NavItem T={T} active={route.kind === 'home' && !musicOpen} onClick={() => onNavigate({ kind: 'home' })}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>}
            label="Home"/>

          <NavItem T={T} active={!!weatherOpen} onClick={() => { onOpenWeather?.(); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8.5" cy="8.5" r="3"/><path d="M8.5 2.5v1.5M2.5 8.5h1.5M4.3 4.3l1 1M12.7 4.3l-1 1"/><path d="M9.5 20h8a3.2 3.2 0 0 0 .5-6.35 4.6 4.6 0 0 0-8.75-1.25A3.8 3.8 0 0 0 9.5 20z"/></svg>}
            label="Weather"/>

          <NavItem T={T} active={!!powerOpen} onClick={() => { onOpenPower?.(); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2.5L5 13.5h6l-1 8 8-11h-6l1-8z"/></svg>}
            label="Power"/>

          <NavItem T={T} active={!!plantsOpen} onClick={() => { onOpenPlants?.(); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21v-9"/><path d="M12 12c0-4 3-7 8-7 0 4-3 7-8 7z"/><path d="M12 15c0-3-2.5-5.5-7-5.5 0 3 2.5 5.5 7 5.5z"/></svg>}
            label="Plants"/>

          <NavItem T={T} active={!!poolOpen} onClick={() => { onOpenPool?.(); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 16c1.6 0 2.4-1.2 4-1.2s2.4 1.2 4 1.2 2.4-1.2 4-1.2 2.4 1.2 4 1.2 2.4-1.2 3-1.2"/><path d="M2.5 20c1.6 0 2.4-1.2 4-1.2s2.4 1.2 4 1.2 2.4-1.2 4-1.2 2.4 1.2 4 1.2 2.4-1.2 3-1.2"/><path d="M8 13V5.5a2 2 0 0 1 4 0M16 13V5.5a2 2 0 0 0-4 0M8 8.5h8"/></svg>}
            label="Pool"/>

          <NavItem T={T} active={!!musicOpen} onClick={() => { onOpenMusic?.(null); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
            label="Music"/>

          <NavItem T={T} active={!!builderOpen} onClick={() => { onOpenBuilder?.(null); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>}
            label="Playlist Builder"/>

          <NavItem T={T} active={!!guestOpen} onClick={() => { onOpenGuestMode?.(); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20c.5-3.4 3.2-5.5 6.5-5.5s6 2.1 6.5 5.5"/><circle cx="17" cy="6.5" r="2.4"/><path d="M15.5 13.4c2.6 0 4.5 1.6 5 4.1"/></svg>}
            label="Guest Mode"
            trailing={guestActive ? (
              <Badge T={T}>On</Badge>
            ) : null}/>

          <NavItem T={T} active={!!vacuumOpen} onClick={() => { onOpenVacuum?.(null); onClose(); }}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2"/></svg>}
            label="Vacuum"
            trailing={vacuumBusy ? (
              <Badge T={T}>Out</Badge>
            ) : null}/>

          <div style={{ height: 18 }}/>
          <ALabel T={T} style={{ paddingLeft: 12, marginBottom: 6 }}>Rooms</ALabel>
          {rooms.map(r => {
            const on = (r.lights || []).filter(l => l.on).length;
            const active = route.kind === 'room' && route.id === r.id;
            return (
              <button key={r.id} onClick={() => onNavigate({ kind: 'room', id: r.id })}
                style={{ ...navRow(T, active), justifyContent: 'space-between' }}>
                <span>{r.name}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: fs(T, 13), fontWeight: 400, color: T.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                  {on > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.ink, fontWeight: 500 }}>
                      <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: T.litFill, boxShadow: `0 0 6px 2px ${T.litSoft}` }}/>
                      {on}
                    </span>
                  )}
                  {r.temp != null ? `${r.temp.toFixed(0)}°` : (r.poolTemp != null ? `${r.poolTemp.toFixed(0)}°` : '')}
                </span>
              </button>
            );
          })}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Escape to standard HA — admin-only so kiosk users (when
              ha-controller-proxy ships) can't wander out of the panel.
              Lands on /lovelace; Settings reachable from there. */}
          {hass?.user?.is_admin && (
            <button onClick={() => { window.location.href = '/lovelace'; }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                height: 48, padding: '0 14px', border: 'none',
                background: T.cardSoft, borderRadius: RADIUS.control, cursor: 'pointer',
                color: T.ink, fontFamily: A_FONT.sans, fontWeight: 500,
                fontSize: fs(T, 15), textAlign: 'left',
              }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <path d="M10 17l-5-5 5-5"/>
                <path d="M5 12h12"/>
              </svg>
              Open Home Assistant
              <span style={{ marginLeft: 'auto', fontSize: fs(T, 13), fontWeight: 400, color: T.inkSoft }}>Admin</span>
            </button>
          )}
          <ALabel T={T}>Appearance</ALabel>
          <Segmented T={T} ariaLabel="Appearance" height={40}
            options={[{ value: 'auto', label: 'Auto' }, { value: 'day', label: 'Day' }, { value: 'night', label: 'Night' }]}
            value={autoDark ? 'auto' : dark ? 'night' : 'day'}
            onChange={(v) => {
              if (v === 'auto') { onToggleAuto(true); return; }
              onToggleAuto(false);
              if ((v === 'night') !== dark) onToggleDark();
            }}/>
          <button onClick={onToggleComfort} aria-pressed={comfort} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: RADIUS.control, border: 'none',
            background: T.cardSoft,
            color: T.ink, fontFamily: A_FONT.sans, fontSize: fs(T, 15),
            cursor: 'pointer', textAlign: 'left',
          }}>
            <span style={{
              width: 24, height: 24, borderRadius: 7,
              background: comfort ? T.selected : T.card,
              boxShadow: comfort ? 'none' : `inset 0 0 0 1.5px ${T.hairHi}`,
              color: T.selectedInk, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {comfort && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12l5 5L20 7"/>
                </svg>
              )}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 500 }}>Comfort text</span>
              <span style={{ fontSize: fs(T, 13), color: T.inkSoft, marginTop: 1 }}>
                Larger body text for easier reading
              </span>
            </div>
          </button>
        </div>
      </aside>
    </div>
  );
}

// A menu row: quiet until it is where you are, then a soft fill. Sans
// 16/500 — the menu is read at arm's length, and the old 19px italic
// serif was below the size Cormorant holds up at.
function navRow(T, active) {
  return {
    display: 'flex', alignItems: 'center', gap: 14,
    minHeight: 44, flexShrink: 0, padding: '0 12px', border: 'none', borderRadius: 12,
    background: active ? T.cardSoft : 'transparent',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: active ? 600 : 500,
    color: active ? T.ink : T.inkSoft,
  };
}

function NavItem({ T, active, onClick, icon, label, trailing }) {
  return (
    <button onClick={onClick} style={navRow(T, active)}>
      <span style={{ color: active ? T.ink : T.inkSoft, display: 'flex' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
    </button>
  );
}

// A small state badge beside a menu row (Guest Mode on, the vacuum out).
function Badge({ T, children }) {
  return (
    <span style={{
      fontFamily: A_FONT.sans, fontSize: fs(T, 12), fontWeight: 600,
      color: T.paper, background: T.live,
      padding: '3px 9px', borderRadius: 8,
    }}>{children}</span>
  );
}

export default function App({ hass, narrow }) {
  const vp = useViewport();
  const [autoDark, setAutoDark] = useState(true);
  const [autoVal] = useAutoTheme();

  // Auto-mode prefers HA's own dark setting (so the panel follows the
  // user's HA profile / OS preference). If hass.themes.darkMode is
  // undefined (older HA, or hass not yet pushed), fall back to the local
  // hour-based heuristic.
  const haDark = hass?.themes?.darkMode;
  const autoResolved = haDark != null ? !!haDark : autoVal;

  const [manualDark, setManualDark] = useState(false);
  const dark = autoDark ? autoResolved : manualDark;

  useEffect(() => {
    if (autoDark) setManualDark(autoResolved);
  }, [autoDark, autoResolved]);

  const [comfort, setComfort] = useComfort();
  // Spread scale onto the active palette so every component reading
  // T.scale through `fs(T, base)` picks up Comfort mode without each
  // call-site needing to know about it. 1.15 chosen to make 11/12/14
  // → 13/14/16 — enough lift to be felt with glasses, small enough
  // not to break flex/wrap layouts.
  const T = { ...(dark ? A_DARK : A_LIGHT), scale: comfort ? 1.15 : 1 };

  const {
    rooms, update, updateLight, updateUtility, act, runScene, sendRemoteCommand, sonosCommand, sonosSpeakerCommand,
    appleTvCommand, appleTvText, audioCommand, eqCommand, togglePoolExtension,
    browseSonos, playMedia, getSonosQueue, speakers, poolExtension, mediaPlayer, louvreCommand, destinationsCommand, coverCommand,
  } = usePanelRooms(hass, 'office');
  const [route, setRoute] = useState({ kind: 'home' });

  // Weather — one model for the home card, the room headers' line and
  // the Weather page, from the BOM integration (weather/model.js). The
  // page is a house-wide overlay like Vacuum, opened from the home card
  // or the drawer.
  const now = useClock();
  const weather = useWeather(hass, now);
  const [weatherOpen, setWeatherOpen] = useState(false);
  // Power — Amber's price, today's estimate and the battery for the home
  // card; the page adds Amber's settled history (power/model.js).
  const power = usePower(hass, now);
  const [powerOpen, setPowerOpen] = useState(false);
  // Plants — the four soil-sensor plants; the home line shows only when
  // one is below its watering point (plants/model.js).
  const plants = usePlants(hass);
  const [plantsOpen, setPlantsOpen] = useState(false);
  // Pool — water, cleaner Start/Stop, light and heater; menu only.
  const [poolOpen, setPoolOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // MusicShell is an overlay (not a route) — opens above Home or Room view.
  // focusRoomId scopes the now-playing hero to a specific room (set when
  // the home-view NowPlayingStrip is tapped); null = pick the playing
  // room automatically (drawer Music link opens this way).
  const [musicOpen, setMusicOpen] = useState(false);
  const [musicFocusRoomId, setMusicFocusRoomId] = useState(null);
  const openMusic = (roomId) => {
    setMusicFocusRoomId(roomId || null);
    setMusicOpen(true);
  };

  // BuilderShell — AI playlist builder overlay (folded-in ha-music). Same
  // overlay pattern as MusicShell. focusRoomId defaults the "play here" room
  // to the room you opened it from.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderFocusRoomId, setBuilderFocusRoomId] = useState(null);
  const openBuilder = (roomId) => {
    setBuilderFocusRoomId(roomId || (route.kind === 'room' ? route.id : null));
    setBuilderOpen(true);
  };


  // TVShell — same overlay pattern as MusicShell, room-scoped only
  // (no drawer entry; TV is always opened from a room's Screen tile).
  // PR T1 ports today's AMediaModal Apple TV remote into this full-
  // takeover surface; T2 adds TV-native controls; T3 retires AMediaModal.
  const [tvOpen, setTvOpen] = useState(false);
  const [tvFocusRoomId, setTvFocusRoomId] = useState(null);
  const openTV = (roomId) => {
    if (!roomId) return;
    setTvFocusRoomId(roomId);
    setTvOpen(true);
  };

  // LightsShell / ShadesShell / ClimateShell — final piece of the
  // AModal-retirement initiative (2026-05-24). Each is room-scoped
  // and opens from the corresponding room-tile whole-tile-click.
  const [lightsOpen, setLightsOpen] = useState(false);
  const [lightsFocusRoomId, setLightsFocusRoomId] = useState(null);
  const openLights = (roomId) => {
    if (!roomId) return;
    setLightsFocusRoomId(roomId);
    setLightsOpen(true);
  };
  const [shadesOpen, setShadesOpen] = useState(false);
  const [shadesFocusRoomId, setShadesFocusRoomId] = useState(null);
  const openShades = (roomId) => {
    if (!roomId) return;
    setShadesFocusRoomId(roomId);
    setShadesOpen(true);
  };
  const [climateOpen, setClimateOpen] = useState(false);
  const [climateFocusRoomId, setClimateFocusRoomId] = useState(null);
  const openClimate = (roomId) => {
    if (!roomId) return;
    setClimateFocusRoomId(roomId);
    setClimateOpen(true);
  };

  // GuestModeShell — house-wide mode, not room-scoped. Opens from a
  // drawer entry rather than a room tile. See BRIEF-guest-mode.md.
  const [guestOpen, setGuestOpen] = useState(false);
  const guestActive = hass?.states?.['input_boolean.guest_mode']?.state === 'on';

  // VacuumShell — house-wide like Guest Mode, but also openable from the
  // three room tiles that have vacuum coverage. `vacuumAreas` seeds the
  // selection when opened from a tile (Pavilion seeds five areas, since
  // the panel's rooms are amalgams of the robot's). Null from the drawer,
  // which leaves the previous selection alone.
  const [vacuumOpen, setVacuumOpen] = useState(false);
  const [vacuumAreas, setVacuumAreas] = useState(null);
  const openVacuum = (roomId) => {
    setVacuumAreas(roomId ? areasForPanelRoom(roomId) : null);
    setVacuumOpen(true);
  };
  const vacuumBusy = !['docked', 'unavailable', 'unknown', undefined]
    .includes(hass?.states?.['vacuum.laundry_vacuum']?.state);

  // Close drawer on navigation
  const navigate = (r) => { setRoute(r); setDrawerOpen(false); };

  const room = route.kind === 'room' ? rooms.find(r => r.id === route.id) : null;

  return (
    <div style={{
      width: '100%', height: '100%',
      background: T.paper, color: T.ink,
      overflowY: 'auto',
    }}>
      {route.kind === 'home' ? (
        <HomeView T={T} rooms={rooms} viewport={vp}
          onOpenRoom={(id) => navigate({ kind: 'room', id })}
          onOpenMusic={openMusic}
          onOpenWeather={() => setWeatherOpen(true)} weather={weather}
          onOpenPower={() => setPowerOpen(true)} power={power}
          onOpenPlants={() => setPlantsOpen(true)} plants={plants}
          hass={hass}
          onMenuClick={() => setDrawerOpen(true)}/>
      ) : (
        <RoomView T={T} room={room || rooms[0]} update={update} updateLight={updateLight} updateUtility={updateUtility}
          act={act} runScene={runScene} sendRemoteCommand={sendRemoteCommand} sonosCommand={sonosCommand} sonosSpeakerCommand={sonosSpeakerCommand}
          appleTvCommand={appleTvCommand} appleTvText={appleTvText} audioCommand={audioCommand}
          togglePoolExtension={togglePoolExtension} poolExtension={poolExtension} mediaPlayer={mediaPlayer}
          louvreCommand={louvreCommand} destinationsCommand={destinationsCommand} coverCommand={coverCommand}
          browseSonos={browseSonos} playMedia={playMedia} getSonosQueue={getSonosQueue} speakers={speakers}
          viewport={vp}
          onMenuClick={() => setDrawerOpen(true)}
          onHome={() => navigate({ kind: 'home' })}
          onOpenMusic={openMusic}
          onOpenBuilder={openBuilder}
          onOpenTV={openTV}
          onOpenLights={openLights}
          onOpenShades={openShades}
          onOpenClimate={openClimate}
          onOpenVacuum={openVacuum}
          hass={hass}/>
      )}

      <Drawer T={T} open={drawerOpen} onClose={() => setDrawerOpen(false)}
        rooms={rooms} route={route} onNavigate={navigate}
        onOpenWeather={() => setWeatherOpen(true)} weatherOpen={weatherOpen}
        onOpenPower={() => setPowerOpen(true)} powerOpen={powerOpen}
        onOpenPlants={() => setPlantsOpen(true)} plantsOpen={plantsOpen}
        onOpenPool={() => setPoolOpen(true)} poolOpen={poolOpen}
        onOpenMusic={openMusic} musicOpen={musicOpen}
        onOpenBuilder={openBuilder} builderOpen={builderOpen}
        onOpenGuestMode={() => setGuestOpen(true)} guestOpen={guestOpen} guestActive={guestActive}
        onOpenVacuum={openVacuum} vacuumOpen={vacuumOpen} vacuumBusy={vacuumBusy}
        dark={dark} onToggleDark={() => setManualDark(d => !d)}
        autoDark={autoDark} onToggleAuto={(v) => setAutoDark(v === undefined ? !autoDark : v)}
        comfort={comfort} onToggleComfort={() => setComfort(c => !c)}
        hass={hass}/>

      {/* MusicShell — full-takeover overlay (z=60, above the z=50 Drawer).
          Opens via NowPlayingStrip on home view (focuses that room) or
          the drawer Music link (auto-picks the playing room). hass +
          speakers are passed through for PR 2/3 Spotify + dispatch
          wiring. */}
      <MusicShell T={T} open={musicOpen} onClose={() => setMusicOpen(false)}
        rooms={rooms} focusRoomId={musicFocusRoomId} viewport={vp} hass={hass}
        speakers={speakers} poolExtension={poolExtension}
        sonosCommand={sonosCommand} sonosSpeakerCommand={sonosSpeakerCommand}
        mediaPlayer={mediaPlayer} destinationsCommand={destinationsCommand}
        getSonosQueue={getSonosQueue} eqCommand={eqCommand}
        onOpenBuilder={() => openBuilder(musicFocusRoomId)}/>

      {/* BuilderShell — AI playlist builder overlay (folded-in ha-music).
          Generation works today; Play + Save to Apple Music are gated on the
          Apple Music developer token. */}
      <BuilderShell T={T} open={builderOpen} onClose={() => setBuilderOpen(false)}
        viewport={vp} hass={hass} focusRoomId={builderFocusRoomId} speakers={speakers}/>

      {/* TVShell — full-takeover overlay for the room's Screen tile.
          Always room-scoped (focusRoomId set on open). Per PR T1 this
          ports today's AMediaModal Apple TV remote in a takeover surface;
          T2 adds TV-native controls; T3 retires AMediaModal entirely. */}
      <TVShell T={T} open={tvOpen} onClose={() => setTvOpen(false)}
        rooms={rooms} focusRoomId={tvFocusRoomId} viewport={vp} hass={hass}
        mediaPlayer={mediaPlayer} sendRemoteCommand={sendRemoteCommand}
        appleTvCommand={appleTvCommand} appleTvText={appleTvText}
        audioCommand={audioCommand} eqCommand={eqCommand} act={act} update={update}
        sonosSpeakerCommand={sonosSpeakerCommand}
        destinationsCommand={destinationsCommand}
        togglePoolExtension={togglePoolExtension} poolExtension={poolExtension}/>

      {/* Lighting / Shades / Climate shells — final piece of the
          AModal-retirement initiative. Each is room-scoped and opens
          from the corresponding room-tile click. The body of each is
          the existing modal-body function exported from direction-a.jsx,
          wrapped in TakeoverShell. */}
      <LightsShell T={T} open={lightsOpen} onClose={() => setLightsOpen(false)}
        rooms={rooms} focusRoomId={lightsFocusRoomId} viewport={vp}
        updateLight={updateLight} updateUtility={updateUtility}/>
      <ShadesShell T={T} open={shadesOpen} onClose={() => setShadesOpen(false)}
        rooms={rooms} focusRoomId={shadesFocusRoomId} viewport={vp}
        update={update} louvreCommand={louvreCommand} coverCommand={coverCommand}/>
      <ClimateShell T={T} open={climateOpen} onClose={() => setClimateOpen(false)}
        rooms={rooms} focusRoomId={climateFocusRoomId} viewport={vp}
        update={update}/>

      {/* GuestModeShell — house-wide mode. Opens from the drawer
          NavItem; reads/writes input_boolean.guest_mode +
          input_datetime.guest_mode_{start,end} directly via hass. */}
      <GuestModeShell T={T} open={guestOpen} onClose={() => setGuestOpen(false)}
        hass={hass} viewport={vp}/>

      {/* VacuumShell — spot cleaning only. The whole-house and scheduled
          runs live as automations in ha-vacuum and are deliberately not
          duplicated here. Opens from the drawer, or from the Office /
          Master Bedroom / Pavilion tiles with those rooms preselected. */}
      <VacuumShell T={T} open={vacuumOpen} onClose={() => setVacuumOpen(false)}
        hass={hass} viewport={vp} initialAreas={vacuumAreas}/>

      <WeatherShell T={T} open={weatherOpen} onClose={() => setWeatherOpen(false)}
        weather={weather} viewport={vp} now={now} hass={hass}/>

      <PowerShell T={T} open={powerOpen} onClose={() => setPowerOpen(false)}
        power={power} viewport={vp} hass={hass}/>

      <PlantsShell T={T} open={plantsOpen} onClose={() => setPlantsOpen(false)}
        plants={plants} viewport={vp} hass={hass} now={now}/>

      <PoolShell T={T} open={poolOpen} onClose={() => setPoolOpen(false)}
        viewport={vp} hass={hass}/>
    </div>
  );
}
